import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import PropTypes from "prop-types";
import { ToastContainer } from "react-toastify";
import { CapacityMeter } from "@opendate-app/setlist";
import StatusBadge from "../../shared/StatusBadge";
import DateRangeCell from "../SeriesIndex/components/cells/DateRangeCell";
import CollectionNameCell from "./components/CollectionNameCell";
import CollectionsIndex from "./components/CollectionsIndex";
import "../TeamManager/styles/teamManagerStyles.css";
import "../SeriesIndex/styles/seriesIndexStyles.css";
import "./styles/collectionsIndexStyles.css";

const CollectionsIndexContext = createContext();

export function useCollectionsIndexContext() {
  return useContext(CollectionsIndexContext);
}

const STATUS_LABELS = { active: "Active", archived: "Archived", canceled: "Cancelled" };
const STATUS_BADGE_KEY = { active: "active", archived: "paused", canceled: "canceled" };

const COLUMNS = [
  { data: "series", label: "Collection" },
  { data: "status", label: "Status", orderable: false },
  { data: "date_range", label: "Date range" },
  { data: "events_count", label: "Events", orderable: false },
  { data: "tickets_sold_meter", label: "Tickets sold", orderable: false },
];

const renderInto = (row, className, element) => {
  const cell = row.getElementsByClassName(className)[0];
  if (cell) ReactDOM.render(element, cell);
};

const rowCallback = (row, rowData) => {
  renderInto(row, "series-cell", (
    <CollectionNameCell
      name={rowData.name}
      venueName={rowData.venue_label}
      showUrl={rowData.show_url}
      wholeSeriesCount={Number(rowData.whole_series_count) || 0}
      singleEventCount={Number(rowData.single_event_count) || 0}
    />
  ));
  renderInto(row, "status-cell", (
    <StatusBadge
      status={STATUS_BADGE_KEY[rowData.state_value] || rowData.state_value}
      label={STATUS_LABELS[rowData.state_value] || rowData.state_value}
    />
  ));
  renderInto(row, "date-range-cell", rowData.start_date
    ? <DateRangeCell startDate={rowData.start_date} endDate={rowData.end_date} />
    : <span className="collections-index-muted">Set by its shows</span>);
  renderInto(row, "events-count-cell", <span>{rowData.total_events}</span>);
  renderInto(row, "tickets-sold-meter-cell", Number(rowData.capacity) > 0
    ? <CapacityMeter taken={Number(rowData.tickets_sold) || 0} capacity={Number(rowData.capacity)} label="none" showNumbers />
    : <span className="collections-index-muted">No shows yet</span>);
};

const CollectionsIndexProvider = ({ children, datatablePath, ...props }) => {
  const [activeTab, setActiveTab] = useState("active");
  const [searchTerm, setSearchTerm] = useState("");

  const dataSource = useMemo(() => {
    const params = new URLSearchParams({ state: activeTab });
    if (searchTerm.trim()) params.set("q", searchTerm.trim());
    const separator = datatablePath.includes("?") ? "&" : "?";
    return `${datatablePath}${separator}${params.toString()}`;
  }, [datatablePath, activeTab, searchTerm]);

  const value = {
    ...props,
    activeTab,
    setActiveTab,
    searchTerm,
    setSearchTerm,
    dataSource,
    rowCallback,
    columns: COLUMNS,
  };

  return (
    <CollectionsIndexContext.Provider value={value}>
      {children}
    </CollectionsIndexContext.Provider>
  );
};

CollectionsIndexProvider.propTypes = {
  datatablePath: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const CollectionsIndexApp = ({ csrfToken, ...props }) => {
  useEffect(() => {
    if (csrfToken) axios.defaults.headers.common["X-CSRF-Token"] = csrfToken;
  }, [csrfToken]);

  return (
    <CollectionsIndexProvider {...props}>
      <CollectionsIndex />
      <ToastContainer icon={false} />
    </CollectionsIndexProvider>
  );
};

CollectionsIndexApp.propTypes = {
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  datatablePath: PropTypes.string.isRequired,
  createPath: PropTypes.string.isRequired,
  activeCount: PropTypes.number,
  archivedCount: PropTypes.number,
  canCreateCollection: PropTypes.bool,
  venueOwnerships: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
  csrfToken: PropTypes.string,
};

export default CollectionsIndexApp;
